import { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FiltersContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, UploadCloud, Paperclip, Loader2 } from "lucide-react";
import { UploadDialog } from "@/pages/TieUps";

const EVENT_TYPES = ["Health Camp", "Corporate Wellness", "Society Camp", "Skin Awareness Drive", "Exhibition", "Other"];

const emptyForm = {
  date: todayISO(), executive_id: "", name: "", location: "", event_type: "Health Camp",
  partners_involved: "", footfall: 0, leads_generated: 0, referrals_generated: 0, cost: 0, remarks: "",
};

export default function Events() {
  const { user } = useAuth();
  const { executives, selectedExecId } = useFilters();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [upload, setUpload] = useState(null);

  const load = () => {
    const params = {};
    if (user?.role !== "executive" && selectedExecId !== "all") params.executive_id = selectedExecId;
    api.get("/events", { params }).then((r) => setEvents(r.data));
  };
  useEffect(load, [selectedExecId, user]);

  const submit = async () => {
    if (!form.executive_id || !form.name || !form.location) { toast.error("Fill executive, name, location"); return; }
    setSaving(true);
    try {
      await api.post("/events", { ...form, footfall: Number(form.footfall), leads_generated: Number(form.leads_generated), referrals_generated: Number(form.referrals_generated), cost: Number(form.cost) });
      toast.success("Event saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const openNew = () => {
    setForm({ ...emptyForm, executive_id: user.role === "executive" ? user.executive_id : (executives[0]?.id || "") });
    setOpen(true);
  };

  const execName = (id) => executives.find((e) => e.id === id)?.name || "—";

  const totalFoot = events.reduce((s, e) => s + e.footfall, 0);
  const totalLeads = events.reduce((s, e) => s + e.leads_generated, 0);
  const totalRefs = events.reduce((s, e) => s + e.referrals_generated, 0);
  const totalCost = events.reduce((s, e) => s + e.cost, 0);

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B]">Field Activation</div>
          <h2 className="text-3xl font-light text-white mt-1" style={{ fontFamily: "Outfit" }}>
            Events &amp; Camps Tracker
          </h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="btn-add-event" onClick={openNew}
              className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 text-sm flex items-center gap-2">
              <Plus size={16} /> Log Event
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#0F172A] border-slate-700 text-white max-w-2xl">
            <DialogHeader><DialogTitle>New Event / Camp</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {user?.role !== "executive" && (
                <div className="col-span-2">
                  <label className="text-xs text-slate-400">Executive</label>
                  <Select value={form.executive_id} onValueChange={(v) => setForm({ ...form, executive_id: v })}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 mt-1"><SelectValue placeholder="Choose executive" /></SelectTrigger>
                    <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                      {executives.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {[
                ["date", "Date", "date"], ["name", "Event Name", "text"],
                ["location", "Location", "text"], ["partners_involved", "Partners Involved", "text"],
                ["footfall", "Footfall", "number"], ["leads_generated", "Leads Generated", "number"],
                ["referrals_generated", "Referrals Generated", "number"], ["cost", "Cost (₹)", "number"],
              ].map(([k, label, type]) => (
                <div key={k}>
                  <label className="text-xs text-slate-400">{label}</label>
                  <input type={type} value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: type === "number" ? Number(e.target.value) : e.target.value })}
                    className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#C9A24B]" />
                </div>
              ))}
              <div>
                <label className="text-xs text-slate-400">Type</label>
                <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0F172A] border-slate-700 text-white">
                    {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><label className="text-xs text-slate-400">Remarks</label>
                <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-sm outline-none" rows={2} /></div>
            </div>
            <DialogFooter>
              <button data-testid="event-save" onClick={submit} disabled={saving}
                className="bg-[#C9A24B] hover:bg-[#b08d40] text-[#0F172A] font-semibold rounded-lg px-4 py-2 flex items-center gap-2">
                {saving && <Loader2 size={14} className="animate-spin" />} Save
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Aggregate cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          ["Total Footfall", totalFoot],
          ["Leads Generated", totalLeads],
          ["Referrals from Events", totalRefs],
          ["Total Cost", fmtINR(totalCost)],
        ].map(([label, value]) => (
          <div key={label} className="bg-[#1E293B] border border-slate-800 rounded-xl p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
            <div className="text-white text-2xl font-medium mt-2" style={{ fontFamily: "Outfit" }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Events table */}
      <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-800/50 text-slate-300 text-[11px] uppercase tracking-wider">
                {["Date", "Executive", "Event", "Type", "Location", "Partners", "Footfall", "Leads", "Refs", "Cost", "Files", ""].map((h) => (
                  <th key={h} className="text-left px-3 py-3 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={12} className="p-8 text-center text-slate-400">No events logged.</td></tr>
              ) : events.map((e) => (
                <tr key={e.id} className="border-t border-slate-800 hover:bg-slate-800/30">
                  <td className="px-3 py-2.5">{fmtDate(e.date)}</td>
                  <td className="px-3 py-2.5">{execName(e.executive_id)}</td>
                  <td className="px-3 py-2.5 text-white font-medium">{e.name}</td>
                  <td className="px-3 py-2.5">{e.event_type}</td>
                  <td className="px-3 py-2.5">{e.location}</td>
                  <td className="px-3 py-2.5">{e.partners_involved}</td>
                  <td className="px-3 py-2.5">{e.footfall}</td>
                  <td className="px-3 py-2.5">{e.leads_generated}</td>
                  <td className="px-3 py-2.5">{e.referrals_generated}</td>
                  <td className="px-3 py-2.5">{fmtINR(e.cost)}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-400">
                    {e.attachments?.length > 0 ? <span className="flex items-center gap-1"><Paperclip size={11} /> {e.attachments.length}</span> : "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    <button data-testid={`upload-${e.id}`} onClick={() => setUpload(e)}
                      className="text-xs text-[#C9A24B] hover:text-[#b08d40] flex items-center gap-1">
                      <UploadCloud size={13} /> Attach
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UploadDialog target={upload} onClose={() => { setUpload(null); load(); }} kind="event" />
    </div>
  );
}
